import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Shield, ShieldOff, Search, UserPlus, Crown } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { User } from "@shared/schema";

export default function AdminAccessManagement() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const { data: admins = [], isLoading: adminsLoading } = useQuery<User[]>({
    queryKey: ["/api/admin/admin-users"],
  });

  const { data: searchResults = [], isLoading: searchLoading } = useQuery<User[]>({
    queryKey: [`/api/admin/search-users?q=${encodeURIComponent(debouncedQuery)}`],
    enabled: debouncedQuery.length >= 2,
  });

  const grantMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiRequest("POST", `/api/admin/admin-users/${userId}/grant`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/admin-users"] });
      queryClient.invalidateQueries({ queryKey: [`/api/admin/search-users?q=${encodeURIComponent(debouncedQuery)}`] });
      toast({ title: "Admin access granted", description: "User now has admin privileges." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to grant admin access.", variant: "destructive" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiRequest("POST", `/api/admin/admin-users/${userId}/revoke`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/admin-users"] });
      queryClient.invalidateQueries({ queryKey: [`/api/admin/search-users?q=${encodeURIComponent(debouncedQuery)}`] });
      toast({ title: "Admin access revoked", description: "User no longer has admin privileges." });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to revoke admin access.",
        variant: "destructive",
      });
    },
  });

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      setDebouncedQuery(value);
    }, 300);
    setDebounceTimer(timer);
  };

  const getInitials = (user: User) => {
    const first = user.firstName?.[0] || "";
    const last = user.lastName?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  const getUserDisplay = (user: User) => {
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
    return name || user.email || "Unknown User";
  };

  const nonAdminResults = searchResults.filter(u => u.isAdmin !== "true");

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-wide" data-testid="text-admin-access-title">Admin Access</h1>
        <p className="text-muted-foreground">Manage who has admin access to the platform</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 flex-wrap">
            <Crown className="h-5 w-5" />
            Current Admins
          </CardTitle>
          <CardDescription>
            Users with admin access can manage the platform, verify members, and adjust settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          {adminsLoading ? (
            <div className="text-sm text-muted-foreground py-4">Loading admins...</div>
          ) : admins.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">No admin users found.</div>
          ) : (
            <div className="space-y-3">
              {admins.map((admin) => (
                <div
                  key={admin.id}
                  className="flex items-center justify-between gap-4 p-3 rounded-md border flex-wrap"
                  data-testid={`admin-user-${admin.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-wrap">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarImage src={admin.profileImageUrl || undefined} />
                      <AvatarFallback>{getInitials(admin)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="font-medium truncate" data-testid={`text-admin-name-${admin.id}`}>
                        {getUserDisplay(admin)}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {admin.email || "No email"}
                      </p>
                    </div>
                    {admin.userType && (
                      <Badge variant="secondary" className="shrink-0">
                        {admin.userType}
                      </Badge>
                    )}
                    {admin.company && (
                      <span className="text-sm text-muted-foreground truncate hidden sm:inline">
                        {admin.company}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => revokeMutation.mutate(admin.id)}
                    disabled={revokeMutation.isPending}
                    data-testid={`button-revoke-admin-${admin.id}`}
                  >
                    <ShieldOff className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 flex-wrap">
            <UserPlus className="h-5 w-5" />
            Add Admin
          </CardTitle>
          <CardDescription>
            Search for a user by name, email, or company to grant them admin access
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or company..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
              data-testid="input-search-users"
            />
          </div>

          {searchLoading && (
            <div className="text-sm text-muted-foreground py-2">Searching...</div>
          )}

          {debouncedQuery.length >= 2 && !searchLoading && nonAdminResults.length === 0 && (
            <div className="text-sm text-muted-foreground py-2">
              No non-admin users found matching "{debouncedQuery}"
            </div>
          )}

          {nonAdminResults.length > 0 && (
            <div className="space-y-2">
              {nonAdminResults.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between gap-4 p-3 rounded-md border flex-wrap"
                  data-testid={`search-result-${user.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-wrap">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarImage src={user.profileImageUrl || undefined} />
                      <AvatarFallback>{getInitials(user)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{getUserDisplay(user)}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {user.email || "No email"}
                      </p>
                    </div>
                    {user.userType && (
                      <Badge variant="secondary" className="shrink-0">
                        {user.userType}
                      </Badge>
                    )}
                    {user.company && (
                      <span className="text-sm text-muted-foreground truncate hidden sm:inline">
                        {user.company}
                      </span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => grantMutation.mutate(user.id)}
                    disabled={grantMutation.isPending}
                    data-testid={`button-grant-admin-${user.id}`}
                  >
                    <Shield className="h-4 w-4 mr-1" />
                    Make Admin
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
